import { twMerge } from 'tailwind-merge'

const REGIONAL_INDICATOR_A = 0x1f1e6
const LOWERCASE_A = 97
const ALPHABET_LENGTH = 26

/**
 * Converts a flag emoji into its ISO 3166-1 alpha-2 code.
 *
 * A flag emoji is two regional indicator symbols, each one `A`'s offset into
 * U+1F1E6..U+1F1FF, so the code falls out of the code points directly. Doing
 * it this way means the step data keeps its readable emoji and no parallel
 * country-code column has to be maintained alongside it.
 *
 * Returns undefined for anything that is not a flag, which is most of the
 * option emoji in the funnel.
 */
function toCountryCode(emoji: string): string | undefined {
  const offsets = [...emoji].map(
    (character) => (character.codePointAt(0) ?? 0) - REGIONAL_INDICATOR_A,
  )

  if (offsets.length !== 2) return undefined

  if (offsets.some((offset) => offset < 0 || offset >= ALPHABET_LENGTH)) {
    return undefined
  }

  return offsets
    .map((offset) => String.fromCharCode(LOWERCASE_A + offset))
    .join('')
}

interface Props {
  className?: string
  emoji: string
  /** Side of the circular flag, in px. Also the font size of the fallback. */
  size?: number
}

/**
 * An option's leading icon: a circular SVG flag when the emoji is a flag, and
 * the emoji itself otherwise.
 *
 * Flag emoji are the wrong tool for a language picker. They render at
 * inconsistent sizes, they are styled by the OS rather than by us, and on
 * Windows they come out as bare letter pairs ("GB") because the platform has
 * no flag glyphs at all. Every current language app — Babbel, Tabby,
 * CapWords, Polarsteps — crops a real flag into a circle instead.
 */
export default function Flag({ className, emoji, size = 32 }: Props) {
  const countryCode = toCountryCode(emoji)

  if (!countryCode) {
    return (
      <span aria-hidden className="leading-none" style={{ fontSize: size }}>
        {emoji}
      </span>
    )
  }

  // `fis` selects flag-icons' 1x1 artwork, which is already square, so its own
  // `background-size: contain` fills the box exactly and `rounded-full` crops
  // it to a circle. Don't force `cover`: the SVGs carry no intrinsic pixel
  // size, so the browser scales them to the larger axis and crops to the
  // middle of the flag — Japan comes out a plain red disc.
  //
  // The inset ring is load-bearing rather than decorative. These sit on white
  // cards, and the white in a flag is the same white, so without a boundary
  // Japan reads as a floating red dot and the Netherlands as two detached
  // arcs. An inset shadow draws the edge without shrinking the artwork the way
  // a real border would.
  return (
    <span
      aria-hidden
      className={twMerge(
        `fi fis fi-${countryCode} shrink-0 rounded-full shadow-[inset_0_0_0_1px_rgba(0,0,0,0.1)]`,
        className,
      )}
      style={{ height: size, width: size }}
    />
  )
}
